import { notification } from "antd";
import { Submittal } from "../../models/Submittal";

/**
 * Get base of current url => https://app.getplot.com/projects -> https://app.getplot.com
 */
export function baseUrlWithPort(): string {
    return window.location.origin;
}

/**
 * Get the base of current url with an alternate subdomain => https://app.getplot.com/projects -> https://projects.getplot.com
 * @param alt_subdomain alternate subdomain to use
 * @returns
 */
export function baseUrlAltSubdomain(alt_subdomain: string): string {
    const hostname_parts = window.location.host.split(".");
    const new_hostname = [alt_subdomain, hostname_parts[1], hostname_parts[2]].join(".");
    return window.location.protocol + "//" + new_hostname;
}

/**
 * Get the current urls subdomain
 * @returns string of current url subdomain
 */
export function currentUrlSubdomain(): string {
    return window.location.host.split(".")[0];
}

/**
 * Get a URL that redirects to login and then returns back to current page on success
 * @returns URL mapping to login with return url of current url
 */
export function currentUrlRedirectToLogin(): string {
    return urlWithRedirectToLogin(window.location.href);
}

/**
 *
 * @param
 * @returns
 */
export function currentUrlRedirectToSignup(): string {
    return urlWithRedirectToSignup(window.location.href);
}

/**
 * Get a URL that redirects to login before going to the desired page on success
 * @param url
 * @returns URL mapping to login with return url of passed url
 */
export function urlWithRedirectToLogin(url: string): string {
    return baseUrlWithPort() + "/login?return_url=" + url;
}

export function urlWithRedirectToSignup(url: string): string {
    return baseUrlWithPort() + "/signup?return_url=" + url;
}

/**
 * Get the current URL without any added params at the end
 * @returns URL without params
 */
export function currentUrlNoParams(): string {
    return window.location.origin + window.location.pathname;
}

/**
 *
 */
export function homeURLRedirect(): string {
    return baseUrlWithPort() + "/home";
}

export function getSignUpUrl(project_token: string | null, viewed_team: number | null) {
    //Hard coded this instead of fixing routes and redirects. Weird redirect stuff between projects and app subdomain but this works to redirect user BACK to signup flow after signing in.
    let url = `${baseUrlAltSubdomain("project")}/login?return_url=${window.location.href}`;
    if (project_token) {
        url += `&project_token=${project_token}&`;
    }
    if (viewed_team) {
        url += `&team_id=${viewed_team}`;
    }
    return url;
}

/**
 * Will retry a given promise a given number of times as long as that promise returns null, and return the result or null if failed
 * @param func some promised to be repeated
 * @param retryCount how many times to repeat this promise
 * @returns return value of the promise or null if promise returns null
 */
export function retryPromise(func: () => Promise<any>, retryCount: number = 3): Promise<any> {
    if (retryCount <= 0) {
        return Promise.resolve<null>(null);
    }

    return func().then((result) => {
        if (result) {
            return result;
        }

        return retryPromise(func, --retryCount);
    });
}

export function isValidUrl(urlString: string) {
    if (URL.canParse(urlString)) {
        return true;
    }
    return false;
}

export function isHTMLBlock(str: string) {
    const a = document.createElement("div");
    a.innerHTML = str;

    for (let c = a.childNodes, i = c.length; i--; ) {
        if (c[i].nodeType == 1) return true;
    }

    return false;
}

export function isValidURL(str: string) {
    try {
        const url = new URL(str);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

export function integrationSubmittalUrlBuilder(projectInfo: any, submittal: Submittal): string {
    if (projectInfo.integration_source === "procore_sandbox") {
        return `https://sandbox.procore.com/webclients/host/companies/${projectInfo.integration_source_company_id}/projects/${projectInfo.integration_source_project_id}/tools/submittals/${submittal.vendorCache?.vendorRecordId}`;
    } else if (projectInfo.integration_source === "procore") {
        let subdomain = "app";

        /*
        * Yes this logic is insane, but it is based on the following (emailing procore support for clarification):

            Hi Andrew,
            Thank you for reaching out.

            Good question. The subdomain that should be used to view a given item in the Procore web app is dictated by which Procore Region the company in question is located in. This doc from our Dev Portal explains a bit about what the concept of 'Multiple Procore Regions' means and how it works in the context of the API: https://developers.procore.com/documentation/tutorial-mpz

            For the time being, until more regions are added, this is the rule of thumb you can use to determine which subdomain to use when linking to items in Procore:

                If the company ID or project ID is shorter than 15 characters in length (e.g. 270242), the subdomain 'https://app.procore.com' should work
                If the company ID or project ID is 15 characters or more in length (e.g. 562949953425243), the subdomain 'https://us02.procore.com' should work

            Please let me know if you have any further questions.

            Thanks,
            Nick Morrill
            Sr. Integration Engineer
            apisupport@procore.com
        */
        if (
            projectInfo.integration_source_company_id.length >= 15 ||
            projectInfo.integration_source_project_id.length >= 15
        ) {
            subdomain = "us02";
        }

        return `https://app.procore.com/webclients/host/companies/${projectInfo.integration_source_company_id}/projects/${projectInfo.integration_source_project_id}/tools/submittals/${submittal.vendorCache?.vendorRecordId}`;
    } else if (projectInfo.integration_source === "autodesk_construction") {
        return `https://acc.autodesk.com/build/submittals/projects/${projectInfo.integration_source_project_id}/items/${submittal.vendorCache?.vendorRecordId}`;
    } else return "";
}

export function navigatorShare(url: string, title: string) {
    try {
        navigator
            .share({
                title: title,
                url: url,
            })
            .then(() => {
                return true;
            });
    } catch (err) {
        notification.error({
            message: "Error sharing:",
            description: err.toString(),
        });
        return false;
    }
}
